import React from 'react'
import { ApprovalWorkflowSettingsLoader } from './approval-workflow-settings-loader'
import { ViewerLoader } from '../viewer'
import { VulnerabilityBehaviorPanel } from './vulnerability-behavior-panel'
import {
  ApprovalWorkflowSettingsMutation,
  UpdateApprovalWorkflowSettingsMutationArgs
} from './approval-workflow-settings-mutation'
import { hasRole } from '../roles'
import { logError, logSuccess } from '../toaster'
import { SubstatusAliasSettingsLoader } from './substatus-alias-settings-loader'
import {
  SubstatusAliasSettingsMutation,
  SubstatusAliasSettingsMutationArgs
} from './substatus-alias-settings-mutation'
import {
  SubstatusAliasSettingsDeletionMutation,
  SubstatusAliasSettingsDeletionMutationArgs
} from './substatus-alias-settings-deletion'
import { Substatus } from '../vulnerability-status'
import { I18nConsumer } from '../i18n-provider'
import { MutationFn } from 'react-apollo'
import { IntlShape } from 'react-intl'

const diasbledAliasSettings = {
  aliasEnabled: false,
  alias: ''
}

const shouldUpdateAliasSettings = (prevSubstatusAliasSettings, settings) => {
  return prevSubstatusAliasSettings.aliasEnabled === false &&
    settings.alias.length === 0
    ? false
    : true
}

export const onSubmit = async (
  settings,
  organizationUuid: string,
  updateWorkflowSettings: MutationFn<
    {},
    UpdateApprovalWorkflowSettingsMutationArgs
  >,
  prevSubstatusAliasSettings,
  deleteSubstatusAliasSettings: MutationFn<
    {},
    SubstatusAliasSettingsDeletionMutationArgs
  >,
  updateSubstatusAliasSettings: MutationFn<
    {},
    SubstatusAliasSettingsMutationArgs
  >,
  intl: IntlShape,
  refetchWorkflowSettings,
  refetchSubstatusAliasSettings
) => {
  try {
    await updateWorkflowSettings({
      variables: {
        organizationID: organizationUuid,
        enabled: settings.workflowEnabled,
        statuses: settings.statuses,
        severities: settings.severities
      }
    })

    const substatus = Substatus.OTHER.name

    if (shouldUpdateAliasSettings(prevSubstatusAliasSettings, settings)) {
      // eslint-disable-next-line unicorn/prefer-ternary
      if (settings.alias.length === 0) {
        await deleteSubstatusAliasSettings({
          variables: {
            organizationUuid,
            substatus
          }
        })
      } else {
        await updateSubstatusAliasSettings({
          variables: {
            organizationUuid,
            substatus,
            label: settings.alias
          }
        })
      }
      await refetchSubstatusAliasSettings()
    }

    await refetchWorkflowSettings()

    logSuccess(
      intl.formatMessage({
        id: 'detail_enableStatusApprovalWorkflow_successMessage'
      })
    )
  } catch (error) {
    logError(
      intl.formatMessage({
        id: 'detail_enableStatusApprovalWorkflow_errorMessage'
      })
    )
    throw error
  }
}

export function VulnerabilityBehaviorManager() {
  return (
    <ViewerLoader>
      {({ viewer, loading }) => {
        return (
          !loading &&
          !!viewer &&
          hasRole(viewer.currentOrganization.role, 'ADMIN') && (
            <ApprovalWorkflowSettingsLoader
              organizationID={viewer.currentOrganization.id}
            >
              {({ settings, refetch: refetchWorkflowSettings }) => {
                const organizationUuid = viewer.currentOrganization.id

                return (
                  <SubstatusAliasSettingsLoader
                    organizationUuid={organizationUuid}
                  >
                    {({
                      substatusAliasSettings,
                      refetchSubstatusAliasSettings
                    }) => {
                      return (
                        <ApprovalWorkflowSettingsMutation>
                          {(updateWorkflowSettings) => {
                            return (
                              <SubstatusAliasSettingsMutation>
                                {(updateSubstatusAliasSettings) => {
                                  return (
                                    <SubstatusAliasSettingsDeletionMutation>
                                      {(deleteSubstatusAliasSettings) => {
                                        return (
                                          <I18nConsumer>
                                            {({ intl }) => (
                                              <VulnerabilityBehaviorPanel
                                                workFlowSettings={settings}
                                                aliasSettings={
                                                  substatusAliasSettings ||
                                                  diasbledAliasSettings
                                                }
                                                onSubmit={async (settings) => {
                                                  await onSubmit(
                                                    settings,
                                                    organizationUuid,
                                                    updateWorkflowSettings,
                                                    substatusAliasSettings,
                                                    deleteSubstatusAliasSettings,
                                                    updateSubstatusAliasSettings,
                                                    intl,
                                                    refetchWorkflowSettings,
                                                    refetchSubstatusAliasSettings
                                                  )
                                                }}
                                              />
                                            )}
                                          </I18nConsumer>
                                        )
                                      }}
                                    </SubstatusAliasSettingsDeletionMutation>
                                  )
                                }}
                              </SubstatusAliasSettingsMutation>
                            )
                          }}
                        </ApprovalWorkflowSettingsMutation>
                      )
                    }}
                  </SubstatusAliasSettingsLoader>
                )
              }}
            </ApprovalWorkflowSettingsLoader>
          )
        )
      }}
    </ViewerLoader>
  )
}
